import {HoistService, InitContext, LoadSpec, XH} from '@xh/hoist/core';
import {asServerException, getDefaultOptions} from '../../core/ServiceUtils';
import {WatchList, WatchListUpdates} from '../data/DataTypes';
import {observableRef, runInAction} from '@xh/hoist/mobx';

export class WatchListService extends HoistService {
    @observableRef accessor watchLists = [];
    defaultWatchlistId: number;

    override async initAsync(ctx: InitContext): Promise<void> {
        console.log('initializing WatchListService');
        XH.track({category: 'Watchlists', message: 'initializing service'});
        return this.loadAsync();
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        let watchLists: WatchList[];
        try {
            watchLists = await this.fetchWatchlistsAsync();
            this.defaultWatchlistId = (await this.fetchDefaultWatchlistAsync())?.id;
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error loading watchlists'});
        }

        if (watchLists) {
            runInAction(() => {
                this.watchLists = watchLists.map(wl => {
                    return {value: wl.id, label: wl.name};
                });
            });
        }
    }

    private async fetchWatchlistsAsync(fetchOpts = getDefaultOptions()): Promise<WatchList[]> {
        return XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/watchlists`,
            method: 'GET',
            ...fetchOpts
        });
    }

    async fetchWatchlistAsync(watchListId: number, fetchOpts = getDefaultOptions()): Promise<WatchList> {
        return XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/watchlists/${watchListId}`,
            method: 'GET',
            ...fetchOpts
        });
    }

    private async fetchDefaultWatchlistAsync(): Promise<WatchList> {
        return XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/watchlists/default`,
            method: 'GET',
            ...getDefaultOptions()
        });
    }

    async activateWatchlistAsync(watchListId: number): Promise<WatchListUpdates> {
        return XH.fetchService.postJson({
            url: `${XH.getConf('svcApiUrl')}/watchlists/${watchListId}/activate`,
            ...getDefaultOptions()
        });
    }

    async priceWatchlistAsync(watchListId: number): Promise<WatchListUpdates> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/watchlists/${watchListId}/price`,
            ...getDefaultOptions()
        });
    }

    async subscribeWatchlistAsync(watchListId: number): Promise<WatchListUpdates> {
        return XH.fetchService.postJson({
            url: `${XH.getConf('svcApiUrl')}/watchlists/${watchListId}/subscribe`,
            ...getDefaultOptions()
        });
    }

    async saveWatchlistAsync(watchList: WatchList): Promise<WatchList> {
        if (watchList.id) {
            return XH.fetchService.putJson({
                url: `${XH.getConf('svcApiUrl')}/watchlists/${watchList.id}`,
                body: watchList,
                ...getDefaultOptions()
            });
        } else {
            return XH.fetchService.postJson({
                url: `${XH.getConf('svcApiUrl')}/watchlists`,
                body: watchList,
                ...getDefaultOptions()
            });
        }
    }

    async deleteWatchlistAsync(watchListId: number, fetchOpts = getDefaultOptions()): Promise<WatchList> {
        return XH.fetchService.deleteJson({
            url: `${XH.getConf('svcApiUrl')}/watchlists/${watchListId}`,
            ...fetchOpts
        });
    }
}
